import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, PackageX, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatBRL } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import FavoriteButton from "@/components/FavoriteButton";
import Reveal from "@/components/Reveal";

const ProductCard = ({ p, big, testid }) => {
    const { add } = useCart();
    return (
        <article
            data-testid={testid}
            className={`group relative h-full overflow-hidden border border-forge-line bg-forge-panel transition-colors duration-300 hover:border-forge-yellow/70 ${
                big ? "min-h-[320px] md:col-span-2 lg:col-span-2 lg:row-span-2 lg:min-h-[540px]" : "min-h-[300px]"
            }`}
        >
            <Link to="/catalogo" aria-label={p.name} className="absolute inset-0 z-10" data-testid={`${testid}-link`} />
            <img
                src={fileUrl(p.storage_path)}
                alt={p.name}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
            <FavoriteButton product={p} testid={`featured-favorite-${p.id}`} />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-6 pr-16">
                <div>
                    {p.category && (
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-forge-yellow">{p.category}</span>
                    )}
                    <h3 className="mt-1 font-display text-lg font-bold uppercase text-forge-paper md:text-xl">{p.name}</h3>
                    {p.price_cents > 0 && (
                        <span className="mt-1 inline-block font-mono text-xs text-forge-dim">{formatBRL(p.price_cents)}</span>
                    )}
                </div>
                <span className="hidden h-10 w-10 items-center justify-center border border-forge-yellow/50 text-forge-yellow opacity-0 transition-opacity duration-300 group-hover:opacity-100 md:inline-flex">
                    <ArrowRight className="h-4 w-4" />
                </span>
            </div>
            {p.price_cents > 0 && (
                <button
                    onClick={() => {
                        add(p);
                        toast.success(`${p.name} foi para o carrinho.`);
                    }}
                    data-testid={`featured-add-cart-${p.id}`}
                    aria-label={`Adicionar ${p.name} ao carrinho`}
                    className="absolute bottom-5 right-5 z-20 flex h-11 w-11 items-center justify-center bg-forge-yellow text-forge-ink transition-all duration-300 hover:scale-110 md:opacity-0 md:group-hover:opacity-100"
                >
                    <Plus className="h-4 w-4" />
                </button>
            )}
        </article>
    );
};

export const DestaquesSection = () => {
    const { data, isLoading } = useQuery({
        queryKey: ["products"],
        queryFn: async () => (await api.get("/products")).data,
    });
    const products = (data?.products ?? []).slice(0, 3);

    return (
        <section id="destaques" className="py-24 md:py-32">
            <div className="zf-container">
                <Reveal className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <p className="zf-eyebrow">Destaques</p>
                        <h2 className="mt-4 font-display text-3xl font-bold uppercase leading-tight text-forge-paper md:text-5xl">
                            Recém-saídas da forja
                        </h2>
                    </div>
                    <Link
                        to="/catalogo"
                        data-testid="destaques-view-catalog-link"
                        className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-forge-yellow"
                    >
                        Ver catálogo completo
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </Reveal>

                <Reveal delay={0.1} className="mt-12">
                    {isLoading ? (
                        <div className="grid gap-6 md:grid-cols-3">
                            {[0, 1, 2].map((i) => (
                                <div key={i} className="min-h-[300px] animate-pulse bg-forge-panel" />
                            ))}
                        </div>
                    ) : products.length === 0 ? (
                        <div className="grid gap-6 md:grid-cols-3">
                            {["Sua peça aqui", "Em breve", "Fique de olho"].map((title, i) => (
                                <div
                                    key={i}
                                    className="layer-lines flex min-h-[300px] flex-col items-center justify-center gap-3 border border-dashed border-forge-line bg-forge-panel/40 p-8 text-center"
                                    data-testid="destaques-empty-card"
                                >
                                    <PackageX className="h-8 w-8 text-forge-yellow/60" />
                                    <p className="font-display text-sm font-bold uppercase text-forge-paper">{title}</p>
                                    <p className="max-w-[220px] text-xs leading-relaxed text-forge-dim">
                                        O catálogo está em construção — em breve as fotos reais saem da forja.
                                    </p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {products.map((p, i) => (
                                <ProductCard key={p.id} p={p} big={i === 0} testid={`featured-card-${p.id}`} />
                            ))}
                        </div>
                    )}
                </Reveal>
            </div>
        </section>
    );
};

export default DestaquesSection;
